import { Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { useState } from 'react';
import { FormValuesB2B } from '../FormPageB2B';
import { isValidNIP } from '../helpers/validation';
import { dictionaryService } from '../services/dictionaryService';

const CompanyData = ({ formik }: { formik: FormikProps<FormValuesB2B> }) => {
    const { values, handleChange, handleBlur, errors, touched, setFieldValue } = formik;
    const [showGusDialog, setShowGusDialog] = useState(false);
    const [gusLoading, setGusLoading] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState<{ text: string; severity: 'success' | 'warning' | 'error' } | null>(null);

    const formOptions = [
        { label: "Jednoosobowa działalność gospodarcza (JDG)", value: "jdg" },
        { label: "Spółka z ograniczoną odpowiedzialnością", value: "spolka_zoo" },
        { label: "Spółka cywilna", value: "spolka_cywilna" },
        { label: "Spółka jawna", value: "spolka_jawna" },
        { label: "Spółka partnerska", value: "spolka_partnerska" },
        { label: "Spółka komandytowa", value: "spolka_komandytowa" },
        { label: "Spółka komandytowo-akcyjna", value: "spolka_komandytowo_akcyjna" },
        { label: "Prosta spółka akcyjna", value: "prosta_spolka_akcyjna" },
        { label: "Spółka akcyjna", value: "spolka_akcyjna" },
        { label: "Stowarzyszenie", value: "stowarzyszenie" },
        { label: "Fundacja", value: "fundacja" },
    ];

    const handleNipBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        handleBlur(e);
        const nip = e.target.value.replace(/\D/g, '');
        if (isValidNIP(nip)) {
            setShowGusDialog(true);
        }
    };

    const handleGusConfirm = async () => {
        setGusLoading(true);
        try {
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('timeout')), 3000)
            );
            const data = await Promise.race([
                dictionaryService.getNipData(values.nip),
                timeoutPromise,
            ]);
            if (data.name) setFieldValue('companyName', data.name);
            if (data.regon) setFieldValue('regon', data.regon);
            if (data.city) setFieldValue('city', data.city);
            if (data.postalCode) setFieldValue('postalCode', data.postalCode);
            if (data.street) setFieldValue('street', data.street);
            if (data.houseNumber) setFieldValue('houseNumber', data.houseNumber.substring(0, 6));
            if (data.legalForm) setFieldValue('legalForm', data.legalForm);
            setShowGusDialog(false);
            setSnackbarMsg({ text: 'Dane z GUS zostały załadowane', severity: 'success' });
        } catch (err) {
            setShowGusDialog(false);
            if (err instanceof Error && err.message === 'timeout') {
                setSnackbarMsg({ text: 'Nie udało się zaczytać danych z GUS w wymaganym czasie. Proszę wprowadzić dane ręcznie.', severity: 'warning' });
            } else {
                setSnackbarMsg({ text: 'Nie znaleziono danych w GUS dla podanego NIP. Proszę wprowadzić dane ręcznie.', severity: 'error' });
            }
        } finally {
            setGusLoading(false);
        }
    };

    return (
        <>
            <Grid container spacing={6} alignItems="flex-start" direction={{ xs: 'column', sm: 'row' }} sx={{ marginTop: '60px' }}>
                <Grid size={{ xs: 12, sm: 12, md: 5 }} sx={{ height: 'auto' }}>
                    <Typography variant="h6" sx={{ margin: 0, padding: '0px' }}>Dane firmy</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 7 }}>
                    <Stack>
                        <Grid container spacing={4} direction={{ xs: 'column', sm: 'row' }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Nazwa Firmy"
                                    name="companyName"
                                    value={values.companyName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.companyName && Boolean(errors.companyName)}
                                    helperText={touched.companyName && errors.companyName}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="NIP"
                                    name="nip"
                                    value={values.nip}
                                    onChange={handleChange}
                                    onBlur={handleNipBlur}
                                    error={touched.nip && Boolean(errors.nip)}
                                    helperText={touched.nip && errors.nip}
                                    type="tel"
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="REGON"
                                    name="regon"
                                    value={values.regon}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.nip && Boolean(errors.regon)}
                                    helperText={touched.nip && errors.regon}
                                    type="tel"
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Autocomplete
                                    disablePortal
                                    options={formOptions}
                                    getOptionLabel={(option) => option.label}
                                    value={formOptions.find(opt => opt.label === values.legalForm) || null}
                                    onChange={(_, newValue) => setFieldValue('legalForm', newValue?.label || '')}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Forma działalności"
                                            name="legalForm"
                                            onBlur={handleBlur}
                                            error={touched.legalForm && Boolean(errors.legalForm)}
                                            helperText={touched.legalForm && errors.legalForm}
                                        />
                                    )}
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                        </Grid>
                    </Stack>
                </Grid>
            </Grid>

            <Dialog open={showGusDialog} onClose={() => setShowGusDialog(false)}>
                <DialogTitle>Pobierz dane z GUS</DialogTitle>
                <DialogContent>
                    Czy zaczytać dane firmy z rejestru GUS na podstawie podanego NIP?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowGusDialog(false)} disabled={gusLoading}>
                        Nie
                    </Button>
                    <Button onClick={handleGusConfirm} variant="contained" disabled={gusLoading}
                        startIcon={gusLoading ? <CircularProgress size={16} color="inherit" /> : null}>
                        {gusLoading ? 'Pobieranie...' : 'Tak'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snackbarMsg}
                autoHideDuration={6000}
                onClose={() => setSnackbarMsg(null)}
            >
                {snackbarMsg ? (
                    <Alert onClose={() => setSnackbarMsg(null)} severity={snackbarMsg.severity} variant="filled" sx={{ width: '100%' }}>
                        {snackbarMsg.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </>
    );
};

export default CompanyData;
